import React, { useEffect, useMemo, useRef, useState } from "react";
import { AdminShell, WorkerShell } from "@/components/Layout";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Hash, ChatText, PaperPlaneTilt, Users, Lock, House, Wrench, List, X, Camera, Paperclip, File as FileIcon, Bell, BellSlash, Microphone, Pencil, Broadcast, LinkSimple, Star } from "@phosphor-icons/react";
import { enablePushNotifications, pushPermission, pushSupported } from "@/lib/pushNotifications";
import { compressImage } from "@/lib/compress";
import PhotoMarkupModal from "@/components/PhotoMarkupModal";

// iOS-specific PWA detection. Web Push on iPhone ONLY works when the site
// is installed via Share → Add to Home Screen and opened from that icon.
// Regular Safari tabs get NO push — Apple's rule. We detect Safari-tab iOS
// users so we can nudge them to install before promising them a bing.
const isIOS = () => typeof navigator !== "undefined"
  && /iPad|iPhone|iPod/.test(navigator.userAgent)
  && !window.MSStream;
const isStandalone = () => (typeof window !== "undefined")
  && (window.matchMedia?.("(display-mode: standalone)")?.matches
      || window.navigator.standalone === true);

// Chat timestamp — same logic every messenger uses:
//   today       → "2:15 PM"
//   yesterday   → "Yesterday 2:15 PM"
//   this week   → "Mon 2:15 PM"
//   this year   → "Feb 10, 2:15 PM"
//   older       → "Feb 10, 2024 2:15 PM"
function fmtChatTs(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const now = new Date();
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const midnight = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dayDiff = Math.round((midnight(now) - midnight(d)) / 86400000);
  if (dayDiff === 0) return time;
  if (dayDiff === 1) return `Yesterday ${time}`;
  if (dayDiff > 1 && dayDiff < 7) {
    return `${d.toLocaleDateString([], { weekday: "short" })} ${time}`;
  }
  const opts = { month: "short", day: "numeric" };
  if (d.getFullYear() !== now.getFullYear()) opts.year = "numeric";
  return `${d.toLocaleDateString([], opts)} ${time}`;
}

// Day-bucket helpers powering the "─── Today / Yesterday / Mon Sep 22 ───"
// separators injected between chat messages.
function dayBucket(iso) {
  try {
    const d = new Date(iso);
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  } catch { return "unknown"; }
}
function dayLabel(iso) {
  try {
    const d = new Date(iso);
    const now = new Date();
    const midnight = x => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const dayDiff = Math.round((midnight(now) - midnight(d)) / 86400000);
    if (dayDiff === 0) return "Today";
    if (dayDiff === 1) return "Yesterday";
    if (dayDiff > 1 && dayDiff < 7) return d.toLocaleDateString([], { weekday: "long" });
    return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
  } catch { return ""; }
}

// Turn a list of reader user_ids into a compact "Ciarra +2" label using
// the roster we already loaded. Falls back to a numeric count if the
// roster hasn't hydrated yet or the reader isn't in it (e.g. deactivated).
function readerLabel(readerIds, roster) {
  const byId = new Map((roster || []).map(r => [r.id, r]));
  const names = readerIds.map(uid => {
    const r = byId.get(uid);
    return (r?.first || r?.name?.split(" ")[0] || "").trim();
  }).filter(Boolean);
  if (names.length === 0) return `${readerIds.length}`;
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} + ${names[1]}`;
  return `${names[0]} + ${names.length - 1}`;
}

// "Marcus is typing…" / "Marcus and Jhan are typing…" — up to 3 names,
// then "+N more". Server already excludes the caller so we render as-is.
function typingLabel(typers) {
  const names = (typers || []).map(t => t.first || t.name || "").filter(Boolean);
  if (names.length === 0) return "";
  if (names.length === 1) return `${names[0]} is typing…`;
  if (names.length === 2) return `${names[0]} and ${names[1]} are typing…`;
  return `${names[0]}, ${names[1]}, and ${names.length - 2} more are typing…`;
}

/**
 * Team Chat — #team + #office channels + DMs.
 * Mentions:
 *   @FirstName    → user mention (highlighted)
 *   @[Address]    → property pill (workers clickable → property page)
 *   #WO<num>      → workorder pill (workers clickable → job detail)
 * Autocomplete triggers on `@` (users + properties) and `#` (workorders).
 */
export default function TeamChat() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAdmin = user?.role === "admin";
  const isWorker = user?.role === "worker";
  const Shell = isAdmin ? AdminShell : WorkerShell;

  const [threads, setThreads] = useState({ channels: [], dms: [] });
  const [roster, setRoster] = useState([]);
  // ClickUp Chat mirror — per-admin, pass-through to CU's Chat API v3.
  // Only rendered when the admin has bound their CLICKUP_PERSONAL_TOKEN.
  const [cuStatus, setCuStatus] = useState(null);   // {ok, using} | {ok:false, reason}
  const [cuChannels, setCuChannels] = useState([]); // [{id,name,type,last_message_at,counterparts:[],hidden}]
  const [cuMe, setCuMe] = useState("");             // this admin's CU user_id (used to flip "mine")
  const [cuSection, setCuSection] = useState({ dm: true, group: true, channel: false }); // collapsible groups
  const [cuShowAll, setCuShowAll] = useState({ dm: false, group: false, channel: false });
  const [cuShowHidden, setCuShowHidden] = useState(false);  // reveal ✕-ed rows for un-hide
  const [cuSearch, setCuSearch] = useState("");  // sidebar filter for CU channels
  const [cuGlobalHits, setCuGlobalHits] = useState(null);   // { channels, tasks, comments } from /search
  const [cuBusy, setCuBusy] = useState(false);
  // Photo markup — user tapped the pencil overlay on a chat image. Modal
  // loads the URL, tech draws on it, on Save we get a Blob back and re-post
  // it to the current thread as a new message (with a "🖍 Markup" caption).
  const [markupSrc, setMarkupSrc] = useState(null);
  // Lightbox — click any image to full-screen. Stores the list of srcs
  // in the current thread + the active index so ←/→ paginate through them.
  const [lightbox, setLightbox] = useState(null); // { srcs: [...], index: 0 }
  const [active, setActive] = useState({ kind: "channel", key: "team", name: "#team" });
  const [msgs, setMsgs] = useState([]);
  // Bumped on every successful send; loadMsgs skips its setMsgs if this
  // counter changes between fetch start and finish (i.e. a send landed
  // during an in-flight poll). Fixes the "message shows, chimes, then
  // clears from chat" bug caused by a stale 15s poll response.
  const sendSeqRef = useRef(0);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [dmPickerOpen, setDmPickerOpen] = useState(false);
  const [pickedProps, setPickedProps] = useState([]); // [{address,key}]
  const [pickedJobs, setPickedJobs] = useState([]);   // [{id,wo,title,address}]
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Pending attachments — File objects the user picked but hasn't sent yet.
  // Uploaded on send() so a canceled message doesn't leave orphan files.
  const [pending, setPending] = useState([]); // [{ file, previewUrl, kind }]
  const cameraRef = useRef(null);
  const attachRef = useRef(null);
  // ── Typing indicators — 2s heartbeat while composing so office sees
  // "Marcus is typing…" pips. Everyone else on the thread polls typing
  // state on their normal 5s poll.
  const [typers, setTypers] = useState([]); // [{ user_id, first, name }]
  const typingHeartbeatRef = useRef(0);
  const sendTypingHeartbeat = () => {
    // ClickUp mirror doesn't expose a typing-indicator endpoint — no-op
    if (active.kind === "clickup") return;
    const now = Date.now();
    // Throttle to at most one POST every 2s — the endpoint upserts, so
    // hammering it costs the server nothing but the network bytes.
    if (now - typingHeartbeatRef.current < 2000) return;
    typingHeartbeatRef.current = now;
    const payload = (active.kind === "channel" || active.kind === "personal")
      ? { kind: "channel", channel: active.key }
      : { kind: "dm", other_user_id: active.otherUserId };
    api.post("/team-chat/typing", payload).catch(() => {});
  };

  // ── Voice notes — long-press the mic to record, release to send.
  // Techs asked for this so they can leave a memo when their hands are
  // dirty ("greasy from HVAC, can't type"). Uses MediaRecorder — falls
  // back gracefully on browsers that don't support it.
  const [recording, setRecording] = useState(false);
  const [recordingSecs, setRecordingSecs] = useState(0);
  const mediaRecorderRef = useRef(null);
  const recordChunksRef = useRef([]);
  const recordTickerRef = useRef(null);
  const recordStartAtRef = useRef(0);
  const recordCancelledRef = useRef(false);
  const MAX_VOICE_SECONDS = 60; // techs asked for 15s but MediaRecorder is per-frame; hard-cap 60s to keep uploads sane
  const startVoiceRecord = async () => {
    if (recording) return;
    if (!navigator?.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error("This browser can't record voice memos");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Pick a mime type MediaRecorder actually supports on this UA. Chrome +
      // Firefox + Android land on webm/opus; Safari lands on mp4/aac.
      let mimeType = "";
      const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
      for (const c of candidates) {
        if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(c)) { mimeType = c; break; }
      }
      const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      recordChunksRef.current = [];
      recordCancelledRef.current = false;
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) recordChunksRef.current.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        if (recordCancelledRef.current) {
          recordChunksRef.current = [];
          return;
        }
        const type = mr.mimeType || "audio/webm";
        const ext = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
        const blob = new Blob(recordChunksRef.current, { type });
        recordChunksRef.current = [];
        if (blob.size < 500) { toast.error("Recording too short — hold to record"); return; }
        const file = new File([blob], `voice-${Date.now()}.${ext}`, { type });
        // Push into the pending-attachments queue so the existing send flow
        // handles upload + posting. No caption required for voice notes.
        setPending(prev => [...prev, {
          file,
          previewUrl: URL.createObjectURL(blob),
          kind: "audio",
          lat: null, lng: null, accuracy: null,
        }]);
        toast.success("Voice memo ready — hit send");
      };
      mr.start(100);
      mediaRecorderRef.current = mr;
      recordStartAtRef.current = Date.now();
      setRecording(true);
      setRecordingSecs(0);
      recordTickerRef.current = setInterval(() => {
        const secs = Math.floor((Date.now() - recordStartAtRef.current) / 1000);
        setRecordingSecs(secs);
        if (secs >= MAX_VOICE_SECONDS) stopVoiceRecord();
      }, 200);
    } catch (e) {
      toast.error("Mic permission denied — enable in browser settings");
    }
  };
  const stopVoiceRecord = () => {
    if (recordTickerRef.current) { clearInterval(recordTickerRef.current); recordTickerRef.current = null; }
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") {
      try { mr.stop(); } catch {}
    }
    setRecording(false);
  };
  const cancelVoiceRecord = () => {
    recordCancelledRef.current = true;
    stopVoiceRecord();
    toast.info("Recording cancelled");
  };
  // Emoji reaction picker — { messageId } when open, null when closed.
  // Ciarra asked for 1-tap ack so office can confirm receipt on photos
  // without typing back a message.
  const [reactPickerFor, setReactPickerFor] = useState(null);
  const REACTIONS = ["👍", "❤️", "😂", "🚨", "✅"];
  const toggleReact = async (m, emoji) => {
    if (!m?.id || !emoji) return;
    // Optimistic update — flip the emoji list locally so the tap feels
    // instant on cell data. Server response replaces it a few 100ms later.
    setMsgs(prev => prev.map(x => {
      if (x.id !== m.id) return x;
      const r = { ...(x.reactions || {}) };
      const list = new Set(r[emoji] || []);
      if (list.has(user?.id)) list.delete(user.id); else list.add(user.id);
      if (list.size) r[emoji] = Array.from(list); else delete r[emoji];
      return { ...x, reactions: r };
    }));
    setReactPickerFor(null);
    try {
      const { data } = await api.post(`/team-chat/messages/${m.id}/react`, { emoji });
      setMsgs(prev => prev.map(x => x.id === m.id ? { ...x, reactions: data.reactions || {} } : x));
    } catch (e) {
      toast.error("Couldn't react — try again");
      // Roll back the optimistic flip on failure
      loadMsgs();
    }
  };

  // 🔔 Audible chime on new incoming messages. Muted state is per-device
  // (localStorage) so one crew member can silence their phone without
  // dragging everyone else with them.
  const [soundOn, setSoundOn] = useState(() => {
    try { return localStorage.getItem("fr_chat_sound_on") !== "0"; } catch { return true; }
  });
  const lastSeenIdRef = useRef(null);
  const audioCtxRef = useRef(null);
  const playChime = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const now = ctx.currentTime;
      // Two-tone "ding" — 880Hz then 1320Hz, ~200ms total. Gentle envelope.
      [[880, now, 0.08], [1320, now + 0.08, 0.12]].forEach(([freq, start, dur]) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, start);
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.25, start + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
        osc.connect(gain).connect(ctx.destination);
        osc.start(start);
        osc.stop(start + dur + 0.02);
      });
    } catch { /* older browsers — silent */ }
  };
  const toggleSound = () => {
    setSoundOn(prev => {
      const next = !prev;
      try { localStorage.setItem("fr_chat_sound_on", next ? "1" : "0"); } catch {}
      if (next) playChime(); // preview so user knows it worked
      return next;
    });
  };

  // Autocomplete state
  const [acOpen, setAcOpen] = useState(false);
  const [acKind, setAcKind] = useState(null); // "user_or_prop" | "workorder"
  const [acQ, setAcQ] = useState("");
  const [propMatches, setPropMatches] = useState([]);
  const [jobMatches, setJobMatches] = useState([]);

  const listRef = useRef(null);
  const inputRef = useRef(null);

  const loadThreads = async () => {
    try {
      const [t, r] = await Promise.all([
        api.get("/team-chat/threads"),
        api.get("/team-chat/roster"),
      ]);
      setThreads(t.data);
      setRoster(r.data);
      setFavorites(new Set(t.data.favorites || []));
    } catch (e) { /* silent */ }
  };

  // Per-user chat favorites. Key shapes:
  //   channel:<key>  (e.g. channel:team, channel:office, channel:my_clickup)
  //   dm:<other_user_id>
  //   cu:<clickup_channel_id>
  const [favorites, setFavorites] = useState(new Set());
  const isFavored = (key) => favorites.has(key);
  const toggleFavorite = async (key, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    if (!key) return;
    const willFavor = !favorites.has(key);
    // Optimistic UI — flip the star instantly so the click feels responsive
    setFavorites(prev => {
      const next = new Set(prev);
      if (willFavor) next.add(key); else next.delete(key);
      return next;
    });
    try {
      const { data } = await api.post("/team-chat/favorites/toggle",
                                       { key, favored: willFavor });
      if (Array.isArray(data.favorites)) setFavorites(new Set(data.favorites));
    } catch (err) {
      toast.error("Couldn't save favorite — try again");
      // Roll back
      setFavorites(prev => {
        const next = new Set(prev);
        if (willFavor) next.delete(key); else next.add(key);
        return next;
      });
    }
  };

  // ── ClickUp Chat mirror loaders ─────────────────────────────────────
  const loadCuStatus = async () => {
    if (!isAdmin) { setCuStatus(null); return; }
    try {
      const { data } = await api.get("/clickup/chat/status");
      setCuStatus(data);
    } catch (e) {
      setCuStatus({ ok: false, reason: "error" });
    }
  };
  const loadCuChannels = async () => {
    if (!isAdmin) { setCuChannels([]); return; }
    try {
      const { data } = await api.get("/clickup/chat/channels", { params: { limit: 100 } });
      setCuChannels(Array.isArray(data.channels) ? data.channels : []);
    } catch (e) { /* silent — token likely missing */ }
  };
  const purgeUnknownDms = async () => {
    if (!isAdmin) return;
    if (!confirm("Delete every DM thread where the other person's account no longer exists?\n\nThis clears the 'Unknown' entries from your sidebar. You can't undo this.")) return;
    try {
      const { data } = await api.post("/team-chat/dm/purge-unknown");
      toast.success(`Cleaned up ${data.threads || 0} orphaned DM thread(s)`);
      await loadThreads();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not purge");
    }
  };

  // ── Hide/un-hide a CU DM or channel from THIS admin's sidebar
  // (doesn't touch ClickUp, doesn't touch anyone else's view). Toggles
  // optimistically then reconciles with the /channels response.
  const toggleCuHide = async (channelId, nextHidden) => {
    setCuChannels(prev => prev.map(c =>
      c.id === channelId ? { ...c, hidden: nextHidden } : c
    ));
    try {
      await api.post(`/clickup/chat/channels/${channelId}/hide`, { hide: nextHidden });
      toast.success(nextHidden ? "Hidden from your sidebar" : "Un-hidden");
    } catch (e) {
      // Revert on failure
      setCuChannels(prev => prev.map(c =>
        c.id === channelId ? { ...c, hidden: !nextHidden } : c
      ));
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update");
    }
  };

  const loadMsgs = async () => {
    try {
      // ClickUp Chat mirror branch — pass-through fetch, no local storage
      if (active.kind === "clickup") {
        const seqBefore = sendSeqRef.current;
        const { data } = await api.get(`/clickup/chat/channels/${active.cuChannelId}/messages`, { params: { limit: 80 } });
        if (sendSeqRef.current !== seqBefore) return;
        const me = data.cu_me || cuMe;
        if (me && me !== cuMe) setCuMe(me);
        // Clean CU markdown before render. ClickUp stores @-mentions as
        //   [@Andrew Harman](#user_mention #55483526)
        // and attaches other bracketed refs. Users don't need to see the
        // raw ids — strip the `(...)` block whenever it looks like a
        // ClickUp internal ref (starts with `#user_mention`, `#task_mention`,
        // `#doc_mention`, `#channel_mention`, etc.).
        const cleanCu = (raw) => {
          if (!raw) return "";
          // 0) Markdown image syntax `![alt](url)` — CU chat inlines its
          //    own hosted images as raw markdown. We want the image to
          //    render as an attachment bubble, NOT as a big string of
          //    `![image.png](https://…)` text under the message. Drop the
          //    whole match (extractImages runs on the pre-clean text below
          //    so the URL is still captured for the attachment renderer).
          let out = raw.replace(/!\[[^\]]*\]\([^)]+\)/g, "").trim();
          // 1) [label](#*_mention #digits...) — drop the (...) tail
          out = out.replace(
            /\[([^\]]+)\]\(#(?:user|task|doc|channel|space|folder|list)_mention\s+#[^\)]*\)/gi,
            "$1"
          );
          // 2) [label]( ... #digits ... ) — same shape but paren block is
          //    just an id and/or emoji separators (no `_mention` keyword).
          //    Only match paren blocks that ONLY contain #ids, whitespace
          //    and emojis; don't touch real URLs.
          out = out.replace(
            /\[([^\]]+)\]\(([^\)]*)\)/g,
            (_full, label, inside) => {
              const stripped = inside.replace(/#\d{6,}/g, "")
                                     .replace(/#(?:user|task|doc|channel)_mention/gi, "")
                                     .trim();
              // If what's left is only emoji/punctuation, keep just the label
              if (!stripped || /^[\s\p{P}\p{S}]*$/u.test(stripped)) return label;
              return `[${label}](${inside})`;
            }
          );
          // 3) Bare `#user_mention` / `#digits` runs that occasionally leak
          //    outside the (...) — strip them so we don't render "wrench" chips.
          out = out.replace(/#(?:user|task|doc|channel)_mention/gi, "");
          out = out.replace(/(?:^|\s)#\d{6,}\b/g, "");
          // 4) Collapse double spaces the strips leave behind
          return out.replace(/[ \t]{2,}/g, " ").trim();
        };
        // Extract inline image URLs from CU chat content — anything ending
        // in a common image extension OR a t123.clickup-attachments.com
        // URL — and mount them as attachments so the existing bubble
        // renderer shows the photos inline. Leaves the URL in the text for
        // now (it'll get auto-linkified by the existing text renderer).
        const IMG_RX = /(https?:\/\/[^\s)]+\.(?:png|jpe?g|gif|webp|heic|heif)(?:\?[^\s)]*)?)/gi;
        const CU_ATTACH_RX = /(https?:\/\/(?:t\d+\.clickup-attachments\.com|attachments\.clickup\.com|[^\/\s]+\.clickup\.com\/api\/[^\s)]+))/gi;
        const extractImages = (text) => {
          if (!text) return [];
          const seen = new Set();
          const out = [];
          // CU-hosted images live behind auth on their CDN — route through
          // our /api/clickup/attachment-proxy so the browser can render them
          // without CORS/401 flapping. Backend proxy handles the auth.
          const proxyUrl = (u) => `${API}/clickup/attachment-proxy?url=${encodeURIComponent(u)}`;
          const push = (url) => {
            const clean = url.replace(/[),.]+$/, "");   // trim trailing punctuation
            if (seen.has(clean)) return;
            seen.add(clean);
            out.push({
              id: `cu-att:${clean.slice(-16)}`,
              // Renderer builds `src` from `direct_url` first, then falls back
              // to `${API}/files/${storage_path}` for native FR attachments.
              direct_url: proxyUrl(clean),
              url: clean,
              media_type: "image",
              filename: "photo",
              is_image: true,
            });
          };
          let m;
          IMG_RX.lastIndex = 0;
          while ((m = IMG_RX.exec(text))) push(m[1]);
          CU_ATTACH_RX.lastIndex = 0;
          while ((m = CU_ATTACH_RX.exec(text))) push(m[1]);
          return out;
        };
        // Shape CU messages into the same envelope the existing bubble
        // renderer expects. `author_id === user.id` is what flips a bubble
        // to "mine", so we map the caller's CU user_id → their FR user.id.
        const shaped = (data.messages || []).map(m => {
          const rawText = m.text || "";
          const cleanText = cleanCu(rawText);
          return {
            id: `cu:${m.id}`,
            text: cleanText,
            author_id: (m.author?.id === me) ? (user?.id || "__me__") : `cu:${m.author?.id || "unknown"}`,
            author_name: m.author?.name || "ClickUp user",
            author_role: "",
            created_at: m.created_at,
            read_by: [],
            reactions: {},
            attachments: extractImages(rawText),
            _cu: true,
          };
        });
        setMsgs(shaped);
        return;
      }
      const url = (active.kind === "channel" || active.kind === "personal")
        ? `/team-chat/channel/${active.key}/messages`
        : `/team-chat/dm/${active.otherUserId}/messages`;
      // Snapshot the send counter BEFORE fetching. If a send lands while
      // this request is in flight, its response is stale — skip applying.
      const seqBefore = sendSeqRef.current;
      const { data } = await api.get(url);
      if (sendSeqRef.current !== seqBefore) return;
      // Defensive merge: preserve any local optimistic messages that the
      // server response doesn't include yet (slow mobile / write-visibility
      // lag). If the server has caught up, dedupe by id — server wins.
      setMsgs(prev => {
        const serverIds = new Set(data.map(m => m.id));
        const localOnly = prev.filter(m => m && m.id && !serverIds.has(m.id));
        if (localOnly.length === 0) return data;
        return [...data, ...localOnly].sort((a, b) =>
          String(a.created_at || "").localeCompare(String(b.created_at || ""))
        );
      });
    } catch (e) { /* silent */ }
  };

  useEffect(() => { loadThreads(); loadCuStatus(); loadCuChannels(); }, []);

  // Deep-link from GlobalSearchBar. Supports:
  //   ?cu=<channelId>&cuName=<name>&cuType=<DM|GROUP_DM|CHANNEL>  → open that CU channel
  //   ?channel=my_clickup                                          → open #my-clickup personal feed
  //   ?channel=<key>                                               → open a native FR channel by key
  // Params are consumed once (cleared from URL) so a back-navigation doesn't
  // re-fire the switch.
  useEffect(() => {
    const cuId = searchParams.get("cu");
    if (cuId) {
      const cuName = searchParams.get("cuName") || "ClickUp channel";
      const cuType = searchParams.get("cuType") || "CHANNEL";
      setActive({
        kind: "clickup",
        cuChannelId: cuId,
        key: `cu:${cuId}`,
        name: cuName,
        cuType,
      });
      api.post(`/clickup/chat/channels/${cuId}/mark-seen`).catch(() => {});
      setSearchParams({}, { replace: true });
      return;
    }
    const chan = searchParams.get("channel");
    if (chan === "my_clickup") {
      setActive({ kind: "personal", key: "my_clickup", name: "#my-clickup" });
      setSearchParams({}, { replace: true });
    } else if (chan) {
      setActive({ kind: "channel", key: chan, name: `#${chan}` });
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Debounced global CU search — kicks in at 3+ chars, hits FR jobs + CU
  // chat channels + past #my-clickup comments in ONE call. Results render
  // in the sidebar under the search box, above the channel list.
  useEffect(() => {
    const q = cuSearch.trim();
    if (q.length < 3) { setCuGlobalHits(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/clickup/chat/search",
                                        { params: { q, limit: 10 } });
        if (!cancelled) setCuGlobalHits(data);
      } catch (e) {
        if (!cancelled) setCuGlobalHits(null);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [cuSearch]);
  useEffect(() => {
    if (!active) return;
    // Reset messages when the active thread changes — the "defensive merge"
    // in loadMsgs would otherwise bleed the previous thread's messages into
    // the new one (e.g. clicking a DM would keep showing #team messages).
    setMsgs([]);
    setTypers([]);
    lastSeenIdRef.current = null;
    loadMsgs();
    // Adaptive poll: 5s while the tab is foreground (Jhan reported ~5 min
    // delays before photos showed up — root cause was the flat 15s poll
    // stacking with cellular upload lag). Slow back to 15s when hidden so
    // we don't drain battery in the pocket, and re-hit immediately whenever
    // the tab becomes visible again.
    let timer = null;
    let typingTimer = null;
    const loadTyping = () => {
      const params = (active.kind === "channel" || active.kind === "personal")
        ? { kind: "channel", channel: active.key }
        : { kind: "dm", other_user_id: active.otherUserId };
      api.get("/team-chat/typing", { params })
        .then(r => setTypers(r.data?.typing || []))
        .catch(() => {});
    };
    const schedule = () => {
      if (timer) clearInterval(timer);
      if (typingTimer) clearInterval(typingTimer);
      const visible = typeof document !== "undefined" && document.visibilityState === "visible";
      const ms = visible ? 5000 : 15000;
      timer = setInterval(loadMsgs, ms);
      // Typing pips only refresh when tab is foreground — pointless otherwise
      if (visible) typingTimer = setInterval(loadTyping, 3000);
    };
    const onVis = () => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") { loadMsgs(); loadTyping(); }
      schedule();
    };
    loadTyping();
    schedule();
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVis);
    }
    return () => {
      if (timer) clearInterval(timer);
      if (typingTimer) clearInterval(typingTimer);
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.key, active?.otherUserId, active?.cuChannelId]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [msgs.length]);

  // 🔔 Chime when a new inbound message arrives (author != me). We track the
  // last-seen message id per active thread; on switch, we resync silently.
  useEffect(() => {
    if (msgs.length === 0) { lastSeenIdRef.current = null; return; }
    const last = msgs[msgs.length - 1];
    const prev = lastSeenIdRef.current;
    lastSeenIdRef.current = last.id;
    if (prev == null) return; // first load — don't chime the whole history
    if (last.id === prev) return;
    const mine = last.author_id === user?.id;
    if (!mine && soundOn) playChime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msgs.length, active?.key, active?.otherUserId]);

  useEffect(() => {
    (async () => {
      // ClickUp mirror is pass-through; no local read-tracking endpoint
      if (active.kind === "clickup") return;
      try {
        await api.post("/team-chat/mark-read",
          (active.kind === "channel" || active.kind === "personal")
            ? { kind: "channel", channel: active.key }
            : { kind: "dm", other_user_id: active.otherUserId });
        await loadThreads();
      } catch (e) { /* silent */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.key, active?.otherUserId, active?.cuChannelId, msgs.length]);

  const send = async () => {
    if (!text.trim() && pending.length === 0) return;
    setBusy(true);
    try {
      // ClickUp Chat mirror — text-only, no attachments. Post via the
      // caller's personal token; the reply appears in ClickUp as the
      // admin themselves (not as a bot).
      if (active.kind === "clickup") {
        if (pending.length > 0) {
          toast.error("Attachments aren't supported on ClickUp Chat yet — send text-only");
          setBusy(false); return;
        }
        await api.post(`/clickup/chat/channels/${active.cuChannelId}/messages`, {
          text: text.trim(),
        });
        sendSeqRef.current++;
        setText("");
        setPickedProps([]); setPickedJobs([]); setAcOpen(false);
        await loadMsgs();
        setBusy(false); return;
      }
      // Step 1: upload any pending files first
      let attachments = [];
      if (pending.length > 0) {
        // Client-side image compression — biggest single win on cellular.
        // Raw iPhone photos are 5-10 MB; @0.82/1920px they land ~500 KB with
        // no visible loss. Techs were seeing ~5min upload per pic on cell
        // (Jhan / Ciarra report) — this cuts that to a few seconds.
        // compressImage is a no-op for videos, HEIC, and already-small files.
        const compressed = await Promise.all(pending.map(async p => ({
          ...p,
          file: p.kind === "image" ? await compressImage(p.file) : p.file,
        })));
        const fd = new FormData();
        compressed.forEach(p => {
          fd.append("files", p.file, p.file.name || "photo.jpg");
          // Index-aligned GPS arrays — empty string when we didn't get a fix
          fd.append("lat", p.lat != null ? String(p.lat) : "");
          fd.append("lng", p.lng != null ? String(p.lng) : "");
          fd.append("accuracy", p.accuracy != null ? String(p.accuracy) : "");
        });
        const { data: up } = await api.post("/team-chat/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        attachments = up.attachments || [];
      }
      const url = (active.kind === "channel" || active.kind === "personal")
        ? `/team-chat/channel/${active.key}/messages`
        : `/team-chat/dm/${active.otherUserId}/messages`;
      const body = {
        text: text.trim(),
        mention_props: pickedProps.map(p => ({ address: p.address, key: p.key || "" })),
        mention_jobs: pickedJobs.map(j => ({ id: j.id, wo: j.wo || "", title: j.title || "", address: j.address || "" })),
        attachments,
      };
      const { data } = await api.post(url, body);
      // Bump the send counter so any in-flight loadMsgs() poll (started
      // BEFORE this send hit the DB) will discard its stale response and
      // not wipe the message off screen. Then optimistically append.
      sendSeqRef.current++;
      setMsgs(prev => [...prev, data.message]);
      setText("");
      setPickedProps([]);
      setPickedJobs([]);
      // Revoke preview URLs so we don't leak memory
      pending.forEach(p => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
      setPending([]);
      setAcOpen(false);
      await loadThreads();
      // Re-fetch the authoritative list so we pick up any messages that
      // arrived in the last 15s window (peer replies, system posts) and
      // to reconcile the optimistic append with server ids.
      await loadMsgs();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send");
    } finally { setBusy(false); }
  };

  // Add files from camera or file picker. Caps at 5 per message. For images,
  // we ALSO ask the browser for a GPS fix so the office can see where the
  // photo was taken (Kraaft parity). If the user denies location or the
  // hardware has no fix, we still send the photo — just without the pin.
  const onPickFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const room = 5 - pending.length;
    if (room <= 0) { toast.error("Max 5 files per message"); return; }
    const picked = files.slice(0, room).map(f => ({
      file: f,
      previewUrl: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
      kind: f.type.startsWith("image/") ? "image" : f.type.startsWith("video/") ? "video" : "file",
      lat: null, lng: null, accuracy: null,
    }));
    setPending(prev => [...prev, ...picked]);
    if (files.length > room) toast.error(`Only added first ${room} — 5-file limit`);

    // Async-fetch a single GPS fix and stamp all just-picked photos with it.
    // One fix per pick is plenty — pictures in a batch were taken from the
    // same spot within seconds. Skips silently for non-image picks.
    const hasImages = picked.some(p => p.kind === "image");
    if (hasImages && typeof navigator !== "undefined" && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude, accuracy } = pos.coords || {};
          if (latitude == null || longitude == null) return;
          setPending(prev => prev.map(p => {
            // Stamp only the batch we just added (still no lat/lng and is image)
            if (p.kind === "image" && p.lat == null && picked.includes(p)) {
              return { ...p, lat: latitude, lng: longitude, accuracy: accuracy || null };
            }
            return p;
          }));
        },
        () => { /* denied or no fix — silent */ },
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
      );
    }
  };

  const removePending = (idx) => {
    setPending(prev => {
      const p = prev[idx];
      if (p?.previewUrl) URL.revokeObjectURL(p.previewUrl);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const openDM = (r) => {
    setActive({ kind: "dm", otherUserId: r.id, key: `dm:${r.id}`, name: r.name });
    setDmPickerOpen(false);
    setSidebarOpen(false);
  };

  const openCu = (ch) => {
    // Prefix `cu:` on the key so React re-fires the poll effect and the
    // heading text/subtitle switches cleanly. `cuChannelId` is the raw
    // ClickUp channel id we hit for messages + posting.
    setActive({
      kind: "clickup",
      cuChannelId: ch.id,
      key: `cu:${ch.id}`,
      name: ch.name || (ch.type === "DM" ? "Direct message" : "ClickUp channel"),
      cuType: ch.type || "CHANNEL",
    });
    setSidebarOpen(false);
    // Fire-and-forget: stamp last_seen so the red dot clears on next
    // /channels poll. Also clear the dot locally right away so the UX
    // feels instant.
    if (ch.has_unread) {
      setCuChannels(prev => prev.map(c =>
        c.id === ch.id ? { ...c, has_unread: false } : c
      ));
    }
    api.post(`/clickup/chat/channels/${ch.id}/mark-seen`).catch(() => {});
  };

  // Saves an annotated chat photo — receives a Blob from PhotoMarkupModal
  // (returnBlob:true), uploads it via the same `/team-chat/upload` endpoint
  // the composer uses, and posts it back to the current channel/DM with a
  // "🖍 Markup" caption so it threads with the original in-place.
  const saveMarkupToChat = async ({ blob }) => {
    if (!blob) return;
    try {
      const fd = new FormData();
      fd.append("files", new File([blob], `markup-${Date.now()}.jpg`, { type: "image/jpeg" }));
      fd.append("lat", "");
      fd.append("lng", "");
      fd.append("accuracy", "");
      const { data: up } = await api.post("/team-chat/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const attachments = up.attachments || [];
      const url = (active.kind === "channel" || active.kind === "personal")
        ? `/team-chat/channel/${active.key}/messages`
        : `/team-chat/dm/${active.otherUserId}/messages`;
      const { data } = await api.post(url, { text: "🖍 Markup", attachments, mention_props: [], mention_jobs: [] });
      sendSeqRef.current++;
      setMsgs(prev => [...prev, data.message]);
      toast.success("Markup posted");
      await loadThreads();
      await loadMsgs();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't post markup");
    }
  };

  // Fetch autocomplete matches
  const fetchAc = async (kind, q) => {
    try {
      if (kind === "user_or_prop") {
        const r = await api.get(`/team-chat/mention-suggestions?kind=property&q=${encodeURIComponent(q)}`);
        setPropMatches(r.data);
      } else if (kind === "workorder") {
        const r = await api.get(`/team-chat/mention-suggestions?kind=workorder&q=${encodeURIComponent(q)}`);
        setJobMatches(r.data);
      }
    } catch (e) { /* silent */ }
  };

  // Detect trigger token near caret
  const onTextChange = (e) => {
    const val = e.target.value;
    setText(val);
    // Fire the typing heartbeat — throttled inside sendTypingHeartbeat.
    if (val && val.trim().length > 0) sendTypingHeartbeat();
    const caret = e.target.selectionStart ?? val.length;
    // Look back from caret for @ or # not followed by a space, and not preceded by non-space
    const before = val.slice(0, caret);
    const at = Math.max(before.lastIndexOf("@"), before.lastIndexOf("#"));
    if (at < 0) { setAcOpen(false); return; }
    if (at > 0 && !/\s/.test(before[at - 1])) { setAcOpen(false); return; }
    const trigger = before[at];
    const q = before.slice(at + 1);
    if (q.includes(" ") || q.includes("\n")) { setAcOpen(false); return; }
    const kind = trigger === "#" ? "workorder" : "user_or_prop";
    setAcKind(kind);
    setAcQ(q);
    setAcOpen(true);
    fetchAc(kind, q);
  };

  // Insert selection into text
  const insertToken = (token) => {
    const el = inputRef.current;
    if (!el) return;
    const caret = el.selectionStart ?? text.length;
    const before = text.slice(0, caret);
    const at = Math.max(before.lastIndexOf("@"), before.lastIndexOf("#"));
    if (at < 0) return;
    const next = text.slice(0, at) + token + " " + text.slice(caret);
    setText(next);
    setAcOpen(false);
    setTimeout(() => {
      el.focus();
      const pos = at + token.length + 1;
      el.setSelectionRange(pos, pos);
    }, 0);
  };

  const pickUser = (r) => {
    // The synthetic "everyone" row (only shown to admins) inserts the special
    // @everyone token — the backend resolves it to every tenant user_id.
    if (r.id === "__everyone__") return insertToken("@everyone");
    insertToken(`@${(r.name || "").split(" ")[0]}`);
  };
  const pickProp = (p) => {
    setPickedProps(prev => prev.find(x => x.key === p.key) ? prev : [...prev, p]);
    insertToken(`@[${p.address}]`);
  };
  const pickJob = (j) => {
    setPickedJobs(prev => prev.find(x => x.id === j.id) ? prev : [...prev, j]);
    const label = j.wo ? `#WO${j.wo}` : `#${(j.title || "job").slice(0, 20)}`;
    insertToken(label);
  };

  const userMatches = useMemo(() => {
    if (acKind !== "user_or_prop") return [];
    const q = acQ.toLowerCase();
    // @everyone pings the whole crew — only admins should see it in the picker
    // (backend still resolves the token for anyone, but techs shouldn't spam).
    const everyoneRow = { id: "__everyone__", first: "everyone", name: "everyone", role: "broadcast", office_role: "" };
    const showEveryone = isAdmin && (q === "" || "everyone".startsWith(q) || "here".startsWith(q));
    const list = roster.filter(r =>
      !q || r.first.toLowerCase().startsWith(q) || r.name.toLowerCase().includes(q)
    ).slice(0, 5);
    return showEveryone ? [everyoneRow, ...list].slice(0, 6) : list;
  }, [acKind, acQ, roster, isAdmin]);

  // ⭐ Favorites — unified pinned rows from ALL three sources (native
  // channels + Radar DMs + CU chats). Sorted: unread first, then most
  // recent activity, then name. If the underlying row disappears (e.g. a
  // CU channel gets deleted), the favKey stays in localStorage but the
  // row simply doesn't render.
  const favoriteRows = useMemo(() => {
    const rows = [];
    for (const c of (threads.channels || [])) {
      const favKey = `channel:${c.key}`;
      if (!isFavored(favKey)) continue;
      rows.push({
        favKey, source: "channel", key: c.key, name: c.name,
        unread: c.unread || 0, last_at: "",
        icon: c.key === "office" ? "lock" : "hash",
        onClick: () => {
          const k = c.kind === "personal" ? "personal" : "channel";
          setActive({ kind: k, key: c.key, name: c.name });
          setSidebarOpen(false);
        },
        isActive: (active.kind === "channel" || active.kind === "personal") && active.key === c.key,
      });
    }
    for (const d of (threads.dms || [])) {
      const favKey = `dm:${d.other_user_id}`;
      if (!isFavored(favKey)) continue;
      rows.push({
        favKey, source: "dm", key: `dm:${d.other_user_id}`, name: d.other_name,
        unread: d.unread || 0, last_at: d.last_at || "",
        avatarLetter: (d.other_name || "?")[0].toUpperCase(),
        avatarColor: "#00C4CC",
        onClick: () => openDM({ id: d.other_user_id, name: d.other_name }),
        isActive: active.kind === "dm" && active.otherUserId === d.other_user_id,
      });
    }
    for (const ch of (cuChannels || [])) {
      const favKey = `cu:${ch.id}`;
      if (!isFavored(favKey)) continue;
      const letter = ch.type === "DM"
        ? ((ch.counterparts?.[0]?.initials || (ch.name || "?")[0]) || "?").toUpperCase()
        : "#";
      rows.push({
        favKey, source: "cu", key: `cu:${ch.id}`, name: ch.name || "(no name)",
        unread: ch.has_unread ? 1 : 0, last_at: ch.last_message_at || "",
        avatarLetter: letter.slice(0, 2),
        avatarColor: "#1E3A8A",
        onClick: () => openCu(ch),
        isActive: active.kind === "clickup" && active.cuChannelId === ch.id,
      });
    }
    rows.sort((a, b) => {
      const au = a.unread > 0, bu = b.unread > 0;
      if (au !== bu) return au ? -1 : 1;
      if ((b.last_at || "") !== (a.last_at || ""))
        return (b.last_at || "").localeCompare(a.last_at || "");
      return (a.name || "").localeCompare(b.name || "");
    });
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threads.channels, threads.dms, cuChannels, favorites, active]);

  // ── Web Push — request permission when a tech opens Team Chat. If they've
  // already granted, silently refresh the subscription so a rotated VAPID key
  // takes effect on next server restart.
  const [pushPerm, setPushPerm] = useState(() => pushPermission());
  const [iosBannerDismissed, setIosBannerDismissed] = useState(() =>
    typeof localStorage !== "undefined" && localStorage.getItem("fr_ios_pwa_dismissed") === "1");
  const showIosInstallBanner = isIOS() && !isStandalone() && !iosBannerDismissed;
  useEffect(() => {
    if (!pushSupported()) return;
    if (Notification.permission === "granted") {
      enablePushNotifications().then(() => setPushPerm("granted")).catch(() => {});
    }
  }, []);
  const askForPush = async () => {
    // iPhone-in-Safari-tab can't grant push — send them through Add to Home Screen first.
    if (isIOS() && !isStandalone()) {
      toast.error("iPhone? Tap Share → Add to Home Screen, then open from the icon.");
      setIosBannerDismissed(false);
      return;
    }
    const r = await enablePushNotifications();
    setPushPerm(r);
    if (r === "granted") toast.success("Notifications on — you'll get a bing when office writes");
    else if (r === "denied") {
      // Don't spam a red toast (Chrome won't re-prompt anyway). Show the
      // step-by-step banner instead so techs can actually fix it.
      setDeniedBannerDismissed(false);
      try { localStorage.removeItem("fr_denied_banner_dismissed"); } catch {}
    }
    else if (r === "unsupported") toast.error("This browser can't do notifications yet.");
  };
  const dismissIosBanner = () => {
    setIosBannerDismissed(true);
    try { localStorage.setItem("fr_ios_pwa_dismissed", "1"); } catch {}
  };
  // Once a browser denies notification permission it will NOT re-prompt on
  // subsequent taps — Alejandro / Mykee end up in a loop of tapping "Notify me"
  // and seeing the same red toast. Detect that state and show inline steps
  // to un-block from the browser's own settings panel.
  const [deniedBannerDismissed, setDeniedBannerDismissed] = useState(() =>
    typeof localStorage !== "undefined" && localStorage.getItem("fr_denied_banner_dismissed") === "1");
  const dismissDeniedBanner = () => {
    setDeniedBannerDismissed(true);
    try { localStorage.setItem("fr_denied_banner_dismissed", "1"); } catch {}
  };
  const isAndroidChrome = typeof navigator !== "undefined" &&
    /Android/i.test(navigator.userAgent) && /Chrome/i.test(navigator.userAgent);
  const isDesktopChrome = typeof navigator !== "undefined" &&
    !/Mobi|Android/i.test(navigator.userAgent) && /Chrome/i.test(navigator.userAgent);
  const showDeniedBanner = pushSupported() && pushPerm === "denied" && !deniedBannerDismissed;

  // Message renderer — splits text into pill/text/user-mention segments
  const renderText = (m) => {
    const propsByAddr = {};
    (m.mention_props || []).forEach(p => { propsByAddr[p.address] = p; });
    const jobsByWO = {};
    const jobsByTitle = {};
    (m.mention_jobs || []).forEach(j => {
      if (j.wo) jobsByWO[`WO${j.wo}`] = j;
      if (j.title) jobsByTitle[j.title.slice(0, 20)] = j;
    });
    const myFirst = (user?.name || "").split(" ")[0]?.toLowerCase();

    const openProp = (p) => {
      if (isAdmin) nav(`/admin/properties/${encodeURIComponent(p.address)}`);
      // workers currently can't open properties; keep pill non-nav
    };
    const openJob = (j) => {
      if (!j?.id) return;
      nav(isAdmin ? `/admin/jobs/${j.id}` : `/worker/jobs/${j.id}`);
    };

    // Tokenize: match @[...], #WO..., @word (user), else text
    const tokens = m.text.split(/(@\[[^\]]+\]|#WO[\w\-]+|#[\w\-]+|@[A-Za-z][A-Za-z0-9\-]{1,40})/g);
    return tokens.map((tok, i) => {
      if (!tok) return null;
      // Property pill
      const propM = tok.match(/^@\[([^\]]+)\]$/);
      if (propM) {
        const addr = propM[1];
        const p = propsByAddr[addr] || { address: addr };
        const clickable = isAdmin;
        return (
          <button key={i} onClick={() => clickable && openProp(p)} disabled={!clickable}
            data-testid={`chat-pill-prop-${i}`}
            className={`inline-flex items-center gap-1 h-5 px-1.5 mx-0.5 bg-fuchsia-600 text-white text-[11px] font-black tracking-wider ${clickable ? "hover:bg-fuchsia-700 cursor-pointer" : "cursor-default"}`}>
            <House weight="fill" size={10}/> {addr}
          </button>
        );
      }
      // Workorder pill (with or without WO prefix)
      const woM = tok.match(/^#(WO)?([\w\-]+)$/);
      if (woM) {
        const woNum = woM[2];
        const j = jobsByWO[`WO${woNum}`] || jobsByTitle[woNum] || null;
        const label = j?.wo ? `WO${j.wo}` : `#${woNum}`;
        return (
          <button key={i} onClick={() => j && openJob(j)} disabled={!j}
            data-testid={`chat-pill-job-${i}`}
            title={j ? `${j.title} · ${j.address}` : "Job not found"}
            className={`inline-flex items-center gap-1 h-5 px-1.5 mx-0.5 bg-[#00C4CC] text-white text-[11px] font-black tracking-wider font-mono ${j ? "hover:bg-[#00a8af] cursor-pointer" : "cursor-default opacity-70"}`}>
            <Wrench weight="fill" size={10}/> {label}
          </button>
        );
      }
      // User mention
      if (tok.startsWith("@")) {
        const handle = tok.slice(1).toLowerCase();
        const isBroadcast = handle === "everyone" || handle === "here" || handle === "channel";
        const mine = myFirst && handle === myFirst;
        if (isBroadcast) {
          return <span key={i} className="font-black text-white bg-red-600 px-1.5 py-0.5 rounded">{tok}</span>;
        }
        // Try to resolve the @handle to a real teammate on the roster —
        // matches on first-name (Andrew → Andrew Harman) or on full-name
        // slug ("andrew-harman"). Clicking opens a DM with them.
        const matched = (roster || []).find(r => {
          const nm = (r.name || "").toLowerCase();
          const first = nm.split(/\s+/)[0] || "";
          const slug = nm.replace(/\s+/g, "-");
          return first === handle || slug === handle || nm === handle;
        });
        if (matched) {
          return (
            <button key={i} onClick={() => openDM(matched)}
              data-testid={`chat-mention-user-${matched.id}`}
              title={`DM ${matched.name}`}
              className={`inline align-baseline font-black cursor-pointer transition-colors ${mine ? "text-red-600 bg-yellow-100 px-1 rounded hover:bg-yellow-200" : "text-[#0e7490] hover:text-[#155e75] hover:underline"}`}>
              {tok}
            </button>
          );
        }
        return <span key={i} className={`font-black ${mine ? "text-red-600 bg-yellow-100 px-1 rounded" : "text-[#0e7490]"}`}>{tok}</span>;
      }
      return <span key={i}>{tok}</span>;
    });
  };

  const propResults = acKind === "user_or_prop" ? propMatches.slice(0, 15) : [];
  const jobResults = acKind === "workorder" ? jobMatches.slice(0, 20) : [];

  return (
    <Shell>
      <div className="h-[calc(100vh-1rem)] md:h-screen flex overflow-hidden relative">
        {/* LEFT: threads sidebar — drawer on mobile, static on md+ */}
        <aside className={`${sidebarOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 transition-transform fixed md:relative inset-y-0 left-0 z-40 md:z-auto w-64 flex-shrink-0 bg-slate-50 border-r-2 border-slate-200 overflow-y-auto`}>
          <div className="p-4 flex items-center justify-between">
            <div>
              <div className="font-display font-black text-xl">Team Chat</div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mt-1">Broadcasts + DMs</div>
            </div>
            <button onClick={() => setSidebarOpen(false)}
              className="md:hidden p-1"
              data-testid="chat-sidebar-close">
              <X weight="bold" size={22}/>
            </button>
          </div>
          {favoriteRows.length > 0 && (
            <div className="px-2 mb-2" data-testid="chat-favorites-section">
              <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-amber-500 px-2 py-1">
                <Star weight="fill" size={12}/> Favorites
                <span className="text-slate-400 font-black">({favoriteRows.length})</span>
              </div>
              {favoriteRows.map(r => (
                <div key={r.favKey}
                  className={`group w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-left mb-0.5 transition-colors ${
                    r.isActive ? "bg-[#0f172a] text-white" : "hover:bg-slate-200 text-slate-700"
                  }`}>
                  <button onClick={() => { r.onClick(); }}
                    data-testid={`chat-fav-row-${r.favKey}`}
                    className="flex-1 flex items-center gap-2 min-w-0 text-left">
                    {r.source === "channel" ? (
                      r.icon === "lock"
                        ? <Lock weight="bold" size={14} className="flex-shrink-0"/>
                        : <Hash weight="bold" size={14} className="flex-shrink-0"/>
                    ) : (
                      <div className="relative h-6 w-6 rounded-full text-[10px] font-black flex items-center justify-center flex-shrink-0"
                           style={{ backgroundColor: r.avatarColor, color: "white" }}>
                        {r.avatarLetter}
                        {r.unread > 0 && (
                          <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-red-600 border-2 border-white"/>
                        )}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className={`font-black ${r.source === "channel" ? "uppercase text-xs tracking-widest" : "text-xs"} truncate`}>
                        {r.name}
                      </div>
                      {r.source === "cu" && (
                        <div className={`text-[10px] truncate ${r.isActive ? "text-white/70" : "text-slate-400"}`}>
                          ClickUp
                        </div>
                      )}
                    </div>
                  </button>
                  <button onClick={(e) => toggleFavorite(r.favKey, e)}
                    data-testid={`chat-fav-unpin-${r.favKey}`}
                    title="Un-star"
                    className="text-amber-400 hover:text-amber-500 transition">
                    <Star weight="fill" size={14}/>
                  </button>
                  {r.source !== "cu" && r.unread > 0 && (
                    <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[10px] font-black flex items-center justify-center">
                      {r.unread > 99 ? "99+" : r.unread}
                    </span>
                  )}
                </div>
              ))}
              <div className="border-b border-slate-200 mt-2"/>
            </div>
          )}
          <div className="px-2">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 px-2 py-1">Channels</div>
            {threads.channels.map(c => {
              const favKey = `channel:${c.key}`;
              return (
              <div key={c.key} className={`group w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-left mb-0.5 transition-colors ${
                  (active.kind === "channel" || active.kind === "personal") && active.key === c.key ? "bg-[#0f172a] text-white" : "hover:bg-slate-200 text-slate-700"
                }`}>
                <button onClick={() => {
                    const k = c.kind === "personal" ? "personal" : "channel";
                    setActive({ kind: k, key: c.key, name: c.name });
                    setSidebarOpen(false);
                  }}
                  data-testid={`chan-${c.key}`}
                  className="flex-1 flex items-center gap-2 min-w-0 text-left">
                  {c.key === "office" ? <Lock weight="bold" size={14}/> : <Hash weight="bold" size={14}/>}
                  <span className="font-black uppercase text-xs tracking-widest truncate">{c.name}</span>
                </button>
                <button onClick={(e) => toggleFavorite(favKey, e)}
                  data-testid={`chat-fav-toggle-${favKey}`}
                  title={isFavored(favKey) ? "Un-star" : "Star to pin"}
                  className={`opacity-0 group-hover:opacity-100 focus:opacity-100 ${isFavored(favKey) ? "opacity-100 text-amber-400" : "text-slate-400 hover:text-amber-400"} transition`}>
                  <Star weight={isFavored(favKey) ? "fill" : "regular"} size={14}/>
                </button>
                {c.unread > 0 && (
                  <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[10px] font-black flex items-center justify-center">
                    {c.unread > 99 ? "99+" : c.unread}
                  </span>
                )}
              </div>
              );
            })}
          </div>
          <div className="px-2 mt-4">
            <div className="flex items-center justify-between px-2 py-1">
              <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-[#00C4CC]">
                <Broadcast weight="fill" size={12}/> Radar · Direct
              </div>
              <div className="flex items-center gap-2">
                {isAdmin && (
                  <button onClick={purgeUnknownDms} data-testid="purge-unknown-dms-btn"
                    title="Delete DM threads where the other person's account no longer exists"
                    className="text-[10px] font-black uppercase tracking-widest text-red-600 hover:underline">
                    Clean up
                  </button>
                )}
                <button onClick={() => setDmPickerOpen(true)} data-testid="new-dm-btn"
                  className="text-[10px] font-black uppercase tracking-widest text-[#0e7490] hover:underline">
                  + New
                </button>
              </div>
            </div>
            {threads.dms.length === 0 && (
              <div className="text-[10px] text-slate-400 italic px-2 py-3">
                No DMs yet. Tap + New to start one.
              </div>
            )}
            {threads.dms.map(d => {
              const favKey = `dm:${d.other_user_id}`;
              return (
              <div key={d.other_user_id}
                className={`group w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-left mb-0.5 transition-colors ${
                  active.kind === "dm" && active.otherUserId === d.other_user_id ? "bg-[#0f172a] text-white" : "hover:bg-slate-200 text-slate-700"
                }`}>
                <button onClick={() => openDM({ id: d.other_user_id, name: d.other_name })}
                  data-testid={`dm-${d.other_user_id}`}
                  className="flex-1 flex items-center gap-2 min-w-0 text-left">
                  <div className="h-6 w-6 rounded-full bg-[#00C4CC] text-white text-[10px] font-black flex items-center justify-center flex-shrink-0">
                    {(d.other_name || "?")[0].toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-black text-xs truncate">{d.other_name}</div>
                    <div className="text-[10px] text-slate-400 truncate">{d.last_text}</div>
                  </div>
                </button>
                <button onClick={(e) => toggleFavorite(favKey, e)}
                  data-testid={`chat-fav-toggle-${favKey}`}
                  title={isFavored(favKey) ? "Un-star" : "Star to pin"}
                  className={`opacity-0 group-hover:opacity-100 focus:opacity-100 ${isFavored(favKey) ? "opacity-100 text-amber-400" : "text-slate-400 hover:text-amber-400"} transition`}>
                  <Star weight={isFavored(favKey) ? "fill" : "regular"} size={14}/>
                </button>
                {d.unread > 0 && (
                  <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[10px] font-black flex items-center justify-center">
                    {d.unread > 99 ? "99+" : d.unread}
                  </span>
                )}
              </div>
              );
            })}
          </div>

          {/* ── CLICKUP MIRROR — per-admin, private ────────────────
              Admins who've pasted their personal CU token get a live
              read/write mirror of their ClickUp DMs, Group chats and
              Channels right here. Nobody else on the team sees this.
              Dark-blue branding to visually separate from radar-cyan
              native DMs above. */}
          {isAdmin && (
            <div className="px-2 mt-4" data-testid="cu-chat-section">
              <div className="flex items-center justify-between px-2 py-1">
                <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-[#1E3A8A]">
                  <LinkSimple weight="fill" size={12}/> ClickUp
                  {(() => {
                    const total = cuChannels.filter(c => c.has_unread && !c.hidden).length;
                    if (total === 0) return null;
                    return (
                      <span data-testid="cu-chat-header-unread"
                            className="min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[9px] font-black flex items-center justify-center">
                        {total > 99 ? "99+" : total}
                      </span>
                    );
                  })()}
                </div>
                <button
                  onClick={() => loadCuChannels()}
                  data-testid="cu-chat-refresh"
                  title="Refresh from ClickUp"
                  className="text-[10px] font-black uppercase tracking-widest text-[#1E3A8A] hover:underline">
                  Refresh
                </button>
              </div>
              {/* Search box — filter CU channels by name across all groups.
                  At 3+ chars it ALSO triggers a global search across FR
                  Work Orders + #my-clickup comment history + CU channels.
                  Results render in a floating panel below. */}
              {cuChannels.length >= 5 && (
                <div className="px-2 mb-1">
                  <div className="relative">
                    <input
                      type="text"
                      value={cuSearch}
                      onChange={(e) => setCuSearch(e.target.value)}
                      placeholder="Search chats, WOs, tasks…"
                      data-testid="cu-chat-search"
                      className="w-full h-8 pl-7 pr-7 text-xs font-medium bg-white border-2 border-slate-200 focus:border-[#1E3A8A] focus:outline-none rounded-lg placeholder:text-slate-400"
                    />
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-[13px] pointer-events-none">⌕</span>
                    {cuSearch && (
                      <button
                        onClick={() => { setCuSearch(""); setCuGlobalHits(null); }}
                        data-testid="cu-chat-search-clear"
                        title="Clear search"
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 h-5 w-5 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100 text-xs font-black">
                        ×
                      </button>
                    )}
                  </div>
                  {/* ── Global results panel ─────────────────────
                      Above channel list so hits are the first thing seen.
                      Groups results by kind (WO / Comment) — channels are
                      already filtered in-place below so no duplication. */}
                  {cuGlobalHits && (cuGlobalHits.counts?.tasks > 0 || cuGlobalHits.counts?.comments > 0) && (
                    <div className="mt-2 border-2 border-cyan-200 bg-cyan-50 rounded-lg p-2 space-y-2" data-testid="cu-global-hits">
                      {cuGlobalHits.tasks && cuGlobalHits.tasks.length > 0 && (
                        <div>
                          <div className="text-[9px] font-black uppercase tracking-widest text-cyan-700 mb-1">
                            Work Orders ({cuGlobalHits.tasks.length})
                          </div>
                          {cuGlobalHits.tasks.map(t => (
                            <button
                              key={t.id}
                              onClick={() => nav(`/admin/work-orders/${t.id}`)}
                              data-testid={`cu-hit-task-${t.id}`}
                              className="w-full text-left px-2 py-1.5 rounded hover:bg-white transition-colors">
                              <div className="flex items-center gap-1.5">
                                {t.wo_number && (
                                  <span className="text-[10px] font-black text-cyan-700">#{t.wo_number}</span>
                                )}
                                <span className="text-[10px] font-black text-slate-900 truncate flex-1">{t.name}</span>
                              </div>
                              {t.address && (
                                <div className="text-[9px] text-slate-500 truncate">{t.address}</div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                      {cuGlobalHits.comments && cuGlobalHits.comments.length > 0 && (
                        <div>
                          <div className="text-[9px] font-black uppercase tracking-widest text-cyan-700 mb-1">
                            Comments ({cuGlobalHits.comments.length})
                          </div>
                          {cuGlobalHits.comments.map(c => (
                            <button
                              key={c.id}
                              onClick={() => c.cu_url && window.open(c.cu_url, "_blank")}
                              data-testid={`cu-hit-comment-${c.id}`}
                              className="w-full text-left px-2 py-1.5 rounded hover:bg-white transition-colors">
                              <div className="text-[10px] text-slate-700 line-clamp-2">{c.preview}</div>
                              {c.wo_number && (
                                <div className="text-[9px] font-black text-cyan-700">#{c.wo_number}</div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {cuStatus && !cuStatus.ok && (
                <div className="mx-2 my-2 rounded-lg border-2 border-amber-400 bg-amber-50 overflow-hidden"
                     data-testid="cu-chat-no-token">
                  <div className="bg-amber-400 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-amber-950 flex items-center gap-1.5">
                    <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-amber-950 text-amber-50 text-[9px]">!</span>
                    Connect your ClickUp
                  </div>
                  <div className="px-3 py-3 text-xs text-amber-950 leading-snug">
                    Your ClickUp chats stay locked to <b>your</b> account.
                    Paste your Personal API token once to see your DMs, Groups
                    and Channels here — nobody else's.
                    <div className="mt-2 flex flex-col gap-1.5">
                      <button onClick={() => nav("/admin/clickup?tab=missing&bind=1#personal-token")}
                        data-testid="cu-chat-bind-link"
                        className="h-9 w-full bg-amber-500 hover:bg-amber-600 text-white font-black uppercase text-[11px] tracking-widest rounded flex items-center justify-center gap-1.5">
                        Paste My ClickUp Token →
                      </button>
                      <a href="https://app.clickup.com/settings/apps" target="_blank" rel="noreferrer"
                        data-testid="cu-chat-get-token-link"
                        className="text-[10px] font-black uppercase tracking-widest text-amber-800 underline text-center">
                        Where do I get my token? ↗
                      </a>
                    </div>
                  </div>
                </div>
              )}
              {cuStatus && cuStatus.ok && cuChannels.length === 0 && (
                <div className="text-[10px] text-slate-400 italic px-2 py-3">
                  Loading your ClickUp chats…
                </div>
              )}
              {/* Sub-groups: DMs / Groups / Channels — each collapsible.
                  DMs get truncated to the 6 most-recent by default (users
                  can pile up 20+) with a "Show all" pill. Groups + Channels
                  are usually fewer, but same treatment for consistency.
                  Each row has an ✕ that hides it from THIS admin's sidebar
                  (per-user; doesn't touch CU or anyone else's view). */}
              {["DM", "GROUP_DM", "CHANNEL"].map(t => {
                let allRows = cuChannels.filter(c => t === "CHANNEL" ? (c.type !== "DM" && c.type !== "GROUP_DM") : c.type === t);
                if (allRows.length === 0) return null;
                // Apply search filter — matches on channel name OR any
                // counterpart's name (so searching "Grace" finds a group
                // DM she's in even if the group has a different label).
                const q = cuSearch.trim().toLowerCase();
                if (q) {
                  allRows = allRows.filter(c =>
                    (c.name || "").toLowerCase().includes(q)
                    || (c.counterparts || []).some(cp => (cp.name || "").toLowerCase().includes(q)
                                                         || (cp.email || "").toLowerCase().includes(q))
                  );
                  if (allRows.length === 0) return null;
                }
                const rows = cuShowHidden ? allRows : allRows.filter(c => !c.hidden);
                if (rows.length === 0 && !cuShowHidden) return null;
                const secKey = t === "DM" ? "dm" : t === "GROUP_DM" ? "group" : "channel";
                const label = t === "DM" ? "Direct" : t === "GROUP_DM" ? "Groups" : "Channels";
                // Force-open every group during an active search so results
                // don't hide behind a collapsed section header.
                const open = q ? true : cuSection[secKey];
                const showAll = q ? true : cuShowAll[secKey];   // bypass the 6-row cap while searching
                const CAP = 6;
                const capped = !showAll && rows.length > CAP ? rows.slice(0, CAP) : rows;
                const hiddenCount = rows.length - capped.length;
                return (
                  <div key={t} className="mt-1">
                    <button
                      onClick={() => setCuSection(s => ({ ...s, [secKey]: !s[secKey] }))}
                      data-testid={`cu-chat-group-${secKey}`}
                      className="w-full flex items-center justify-between px-2 py-1 text-[9px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-700">
                      <span>{open ? "▾" : "▸"} {label} ({rows.length})</span>
                    </button>
                    {open && capped.map(ch => {
                      const isActive = active.kind === "clickup" && active.cuChannelId === ch.id;
                      const initials = ch.type === "DM"
                        ? ((ch.counterparts?.[0]?.initials || (ch.name || "?")[0]) || "?").toUpperCase()
                        : "#";
                      return (
                        <div key={ch.id} className={`group relative rounded-lg mb-0.5 transition-colors ${
                          isActive ? "bg-[#1E3A8A]" : ch.hidden ? "opacity-50 hover:bg-slate-200" : "hover:bg-slate-200"
                        }`}>
                          <button
                            onClick={() => openCu(ch)}
                            data-testid={`cu-chat-${ch.id}`}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-left ${
                              isActive ? "text-white" : "text-slate-700"
                            }`}>
                            <div className={`relative h-6 w-6 rounded-full text-[10px] font-black flex items-center justify-center flex-shrink-0 ${
                              isActive ? "bg-white text-[#1E3A8A]" : "bg-[#1E3A8A] text-white"
                            }`}>
                              {initials.slice(0, 2)}
                              {/* Red unread dot — clipped to the avatar corner */}
                              {ch.has_unread && !ch.hidden && (
                                <span data-testid={`cu-chat-${ch.id}-unread-dot`}
                                      className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-red-600 border-2 border-white"/>
                              )}
                            </div>
                            <div className="min-w-0 flex-1 pr-6">
                              <div className="font-black text-xs truncate">
                                {ch.name || "(no name)"}
                                {ch.hidden && <span className={`ml-1 text-[9px] uppercase tracking-widest ${isActive ? "text-white/70" : "text-slate-400"}`}>· hidden</span>}
                              </div>
                              {ch.type === "GROUP_DM" && (
                                <div className={`text-[10px] truncate ${isActive ? "text-white/70" : "text-slate-400"}`}>
                                  {(ch.counterparts || []).slice(0, 3).map(c => c.name).filter(Boolean).join(", ")}
                                </div>
                              )}
                            </div>
                          </button>
                          {/* Star favorite + hide — both revealed on hover. Star
                              on the left of the hide button so they don't
                              overlap. */}
                          <button
                            onClick={(e) => toggleFavorite(`cu:${ch.id}`, e)}
                            data-testid={`chat-fav-toggle-cu:${ch.id}`}
                            title={isFavored(`cu:${ch.id}`) ? "Un-star" : "Star to pin"}
                            className={`absolute right-8 top-1/2 -translate-y-1/2 h-6 w-6 rounded-md inline-flex items-center justify-center transition-opacity ${
                              isFavored(`cu:${ch.id}`) ? "opacity-100 text-amber-400" : "opacity-0 group-hover:opacity-100 focus:opacity-100 text-slate-400 hover:text-amber-400"
                            } ${isActive && !isFavored(`cu:${ch.id}`) ? "text-white/60 hover:text-amber-300" : ""}`}>
                            <Star weight={isFavored(`cu:${ch.id}`) ? "fill" : "regular"} size={14}/>
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleCuHide(ch.id, !ch.hidden); }}
                            data-testid={`cu-chat-${ch.id}-hide`}
                            title={ch.hidden ? "Un-hide" : "Hide from your sidebar"}
                            className={`absolute right-1.5 top-1/2 -translate-y-1/2 h-6 w-6 rounded-md inline-flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-[13px] font-black ${
                              isActive ? "bg-white/20 text-white hover:bg-white/30" : "bg-white text-slate-500 hover:text-slate-900 hover:bg-slate-100 border border-slate-200"
                            }`}>
                            {ch.hidden ? "↺" : "×"}
                          </button>
                        </div>
                      );
                    })}
                    {open && hiddenCount > 0 && (
                      <button
                        onClick={() => setCuShowAll(s => ({ ...s, [secKey]: true }))}
                        data-testid={`cu-chat-show-all-${secKey}`}
                        className="w-full text-[10px] font-black uppercase tracking-widest text-[#1E3A8A] hover:text-[#0F1F3A] hover:bg-slate-100 px-3 py-1.5 rounded-lg mb-0.5">
                        Show {hiddenCount} more…
                      </button>
                    )}
                    {open && showAll && rows.length > CAP && (
                      <button
                        onClick={() => setCuShowAll(s => ({ ...s, [secKey]: false }))}
                        data-testid={`cu-chat-show-less-${secKey}`}
                        className="w-full text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-700 px-3 py-1.5 rounded-lg mb-0.5">
                        Show less
                      </button>
                    )}
                  </div>
                );
              })}
              {/* Global "reveal hidden" toggle — sits at the bottom of the
                  ClickUp section so admins can peek at un-hide anything they
                  regret hiding. Only shows if they actually have hidden rows. */}
              {cuChannels.some(c => c.hidden) && (
                <button
                  onClick={() => setCuShowHidden(v => !v)}
                  data-testid="cu-chat-toggle-hidden"
                  className="mt-2 w-full text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-700 hover:bg-slate-100 px-3 py-1.5 rounded-lg">
                  {cuShowHidden
                    ? "Hide hidden rows"
                    : `Show ${cuChannels.filter(c => c.hidden).length} hidden…`}
                </button>
              )}
              {/* Empty-state for a search that matched nothing across
                  every group. Fires only when there's actually a query. */}
              {cuSearch.trim() && (() => {
                const q = cuSearch.trim().toLowerCase();
                const anyMatch = cuChannels.some(c =>
                  (c.name || "").toLowerCase().includes(q)
                  || (c.counterparts || []).some(cp =>
                       (cp.name || "").toLowerCase().includes(q)
                       || (cp.email || "").toLowerCase().includes(q))
                );
                if (anyMatch) return null;
                return (
                  <div className="mt-2 mx-2 px-3 py-3 rounded-lg bg-slate-50 border border-slate-200 text-[11px] text-slate-500 text-center"
                       data-testid="cu-chat-no-results">
                    No ClickUp chats match "<span className="font-black text-slate-700">{cuSearch}</span>"
                  </div>
                );
              })()}
            </div>
          )}
        </aside>

        {/* Backdrop for mobile drawer */}
        {sidebarOpen && (
          <button onClick={() => setSidebarOpen(false)}
            className="md:hidden fixed inset-0 z-30 bg-black/40"
            aria-label="Close sidebar"/>
        )}

        {/* RIGHT: active thread */}
        <div className="flex-1 flex flex-col min-w-0 bg-white">
          <div className="px-4 md:px-6 py-4 border-b-2 border-slate-100 flex items-center gap-2">
            {/* Mobile: Channels button — replaces the old floating hamburger
                that used to visually overlap the app's main menu button */}
            <button onClick={() => setSidebarOpen(true)}
              data-testid="chat-sidebar-toggle"
              className="md:hidden shrink-0 h-9 px-3 bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-lg font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1.5 border-2 border-slate-300">
              <List weight="bold" size={14}/> Channels
            </button>
            {(active.kind === "channel" || active.kind === "personal") ? (
              active.key === "office" ? <Lock weight="bold" size={22}/> : <Hash weight="bold" size={22}/>
            ) : active.kind === "clickup" ? (
              <span title="ClickUp Chat" className="h-6 px-2 inline-flex items-center rounded-md bg-[#1E3A8A] text-white text-[10px] font-black uppercase tracking-widest">CU</span>
            ) : <ChatText weight="bold" size={22}/>}
            <div className="min-w-0 flex-1">
              <div className="font-display font-black text-lg md:text-xl truncate">
                {(active.kind === "channel" || active.kind === "personal") ? active.name
                  : active.kind === "clickup" ? `${active.cuType === "DM" ? "CU DM" : active.cuType === "GROUP_DM" ? "CU Group" : "CU Channel"} — ${active.name}`
                  : `DM — ${active.name}`}
              </div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 truncate">
                {(active.kind === "channel" || active.kind === "personal")
                  ? (active.key === "office" ? "Admin-only channel" : active.kind === "personal" ? (active.key === "my_clickup" ? "Your private ClickUp mirror — replies post back to ClickUp" : "Personal channel") : "Everyone in the tenant")
                  : active.kind === "clickup" ? "Live ClickUp Chat — replies post to ClickUp as you"
                  : "Private conversation"}
              </div>
            </div>
            {pushSupported() && pushPerm !== "granted" && pushPerm !== "unsupported" && pushPerm !== "denied" && (
              <button type="button" onClick={askForPush} data-testid="push-enable-btn"
                className="tactile-btn h-9 px-3 bg-amber-500 hover:bg-amber-600 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1.5 border-2 border-[#0f172a]">
                <Bell weight="fill" size={14}/> Notify me
              </button>
            )}
            {/* 🔔 Sound toggle — per-device chime on new incoming messages */}
            <button type="button" onClick={toggleSound}
              data-testid="chat-sound-toggle"
              title={soundOn ? "Sound is ON — tap to mute chimes on this device" : "Sound is OFF — tap to hear new-message chimes"}
              className={`tactile-btn h-9 w-9 shrink-0 inline-flex items-center justify-center font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] ${soundOn ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
              {soundOn ? <Bell weight="fill" size={14}/> : <BellSlash weight="fill" size={14}/>}
            </button>
            {pushSupported() && pushPerm === "denied" && (
              <button type="button" onClick={() => { setDeniedBannerDismissed(false); try { localStorage.removeItem("fr_denied_banner_dismissed"); } catch {} }}
                data-testid="push-blocked-btn"
                title="Chrome / browser has blocked notifications. Tap for step-by-step re-enable."
                className="tactile-btn h-9 px-3 bg-red-500 hover:bg-red-600 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1.5 border-2 border-[#0f172a]">
                <Bell weight="fill" size={14}/> Blocked · Fix
              </button>
            )}
            {pushPerm === "granted" && (
              <span data-testid="push-status-on" title="Notifications on — the app will bing your phone even when closed"
                className="hidden sm:inline-flex items-center gap-1 h-8 px-2 bg-emerald-50 text-emerald-800 border border-emerald-200 font-black uppercase text-[9px] tracking-widest">
                <Bell weight="fill" size={12}/> On
              </span>
            )}
          </div>

          {/* Notifications blocked banner — actionable steps for Android/desktop
              Chrome, Firefox etc. Only shows when perm=denied, so the "Notify
              me" toast loop can't repeat itself. */}
          {showDeniedBanner && (
            <div className="px-4 md:px-6 py-3 bg-red-50 border-b-2 border-red-400 flex items-start gap-3" data-testid="push-denied-banner">
              <Bell weight="fill" size={20} className="text-red-600 flex-shrink-0 mt-0.5"/>
              <div className="flex-1 text-xs">
                <div className="font-black text-red-900 uppercase tracking-wide text-[11px]">
                  Notifications are blocked · here's how to turn them back on
                </div>
                {isAndroidChrome ? (
                  <ol className="mt-1 text-red-900/90 leading-snug list-decimal ml-4 space-y-0.5" data-testid="push-denied-android">
                    <li>Tap the <b>⋮</b> menu (top-right, next to the address bar)</li>
                    <li>Tap <b>Site settings</b></li>
                    <li>Tap <b>Notifications</b> → toggle to <b>Allow</b></li>
                    <li>Come back here and tap <b>Blocked · Fix</b> → then this banner will refresh</li>
                  </ol>
                ) : isDesktopChrome ? (
                  <ol className="mt-1 text-red-900/90 leading-snug list-decimal ml-4 space-y-0.5" data-testid="push-denied-desktop">
                    <li>Click the lock 🔒 icon to the left of the URL</li>
                    <li>Set <b>Notifications</b> from Block to <b>Allow</b></li>
                    <li>Refresh the page (⌘R / Ctrl-R)</li>
                  </ol>
                ) : (
                  <ol className="mt-1 text-red-900/90 leading-snug list-decimal ml-4 space-y-0.5" data-testid="push-denied-generic">
                    <li>Open your browser's site settings for <b>fieldradar.pro</b></li>
                    <li>Change <b>Notifications</b> from Block to Allow</li>
                    <li>Refresh this page</li>
                  </ol>
                )}
                <div className="mt-2 text-[10px] text-red-800/80">
                  Once fixed, refresh and the red "Blocked · Fix" chip will flip back to <b>Notify me</b> — tap it to finish enabling.
                </div>
              </div>
              <button onClick={dismissDeniedBanner} data-testid="push-denied-dismiss"
                className="flex-shrink-0 text-red-700 hover:text-red-900 p-1" aria-label="Dismiss">
                <X weight="bold" size={16}/>
              </button>
            </div>
          )}

          {/* iOS-only PWA install nudge — Web Push on iPhone requires
              Add-to-Home-Screen, so nudge Safari-tab users through the flow
              before they hit "Notify me" and get nothing. */}
          {showIosInstallBanner && (
            <div className="px-4 md:px-6 py-3 bg-amber-50 border-b-2 border-amber-300 flex items-start gap-3" data-testid="ios-pwa-banner">
              <Bell weight="fill" size={20} className="text-amber-600 flex-shrink-0 mt-0.5"/>
              <div className="flex-1 text-xs">
                <div className="font-black text-amber-900 uppercase tracking-wide text-[11px]">
                  On iPhone? Install first for notifications
                </div>
                <ol className="mt-1 text-amber-900/90 leading-snug list-decimal ml-4 space-y-0.5">
                  <li>Tap the <b>Share</b> button (square with ↑) at the bottom of Safari</li>
                  <li>Scroll and tap <b>Add to Home Screen</b> → <b>Add</b></li>
                  <li>Close Safari. Open FieldRadar from the new home-screen icon</li>
                  <li>Come back to Team Chat and tap <b>Notify me</b></li>
                </ol>
              </div>
              <button onClick={dismissIosBanner} data-testid="ios-pwa-banner-dismiss"
                className="flex-shrink-0 text-amber-700 hover:text-amber-900 p-1" aria-label="Dismiss">
                <X weight="bold" size={16}/>
              </button>
            </div>
          )}

          <div ref={listRef} className="flex-1 overflow-y-auto p-4 md:p-6 space-y-3" data-testid="team-msgs-list">
            {msgs.length === 0 && (
              <div className="text-center text-slate-400 italic py-12">
                No messages yet. Say hi 👋
              </div>
            )}
            {msgs.map((m, idx) => {
              const mine = m.author_id === user?.id;
              const atts = m.attachments || [];
              // Day separator — insert a compact "Today / Yesterday / Mon Sep 22"
              // divider whenever the day bucket changes between messages.
              const prev = idx > 0 ? msgs[idx - 1] : null;
              const showSeparator = !prev || dayBucket(prev.created_at) !== dayBucket(m.created_at);
              return (
                <React.Fragment key={m.id}>
                  {showSeparator && (
                    <div className="flex items-center gap-2 py-2" data-testid={`team-day-${dayBucket(m.created_at)}`}>
                      <div className="flex-1 h-px bg-slate-200"/>
                      <span className="text-[10px] font-black uppercase tracking-widest text-[#8B93A7] px-2">
                        {dayLabel(m.created_at)}
                      </span>
                      <div className="flex-1 h-px bg-slate-200"/>
                    </div>
                  )}
                <div data-testid={`team-msg-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`flex flex-col max-w-[70%] ${mine ? "items-end" : "items-start"}`}>
                  <div className={`px-3 py-2 rounded-2xl ${
                    mine ? "bg-[#00C4CC] text-white" : "bg-slate-100 text-slate-900"
                  }`}>
                    <div className="text-[10px] font-black uppercase tracking-widest opacity-80">
                      {m.author_name}{m.author_role === "admin" ? " · Office" : ""}
                      {" · "}{fmtChatTs(m.created_at)}
                    </div>
                    {m.text && <div className="text-sm whitespace-pre-wrap">{renderText(m)}</div>}
                    {atts.length > 0 && (
                      <div className={`mt-2 flex flex-wrap gap-1.5 ${m.text ? "" : "mt-1"}`} data-testid={`team-msg-atts-${m.id}`}>
                        {atts.map((a, ai) => {
                          const src = a.direct_url || `${API}/files/${a.storage_path}`;
                          const hasGeo = a.lat != null && a.lng != null;
                          const mapUrl = hasGeo ? `https://www.google.com/maps?q=${a.lat},${a.lng}` : null;
                          if (a.media_type === "image") {
                            // Collect every image src in this thread once so
                            // the lightbox can arrow-key paginate through them.
                            const allSrcs = msgs.flatMap(mm =>
                              (mm.attachments || [])
                                .filter(x => x.media_type === "image")
                                .map(x => x.direct_url || `${API}/files/${x.storage_path}`)
                            );
                            const startIdx = Math.max(0, allSrcs.indexOf(src));
                            return (
                              <div key={ai} className="relative group">
                                <button
                                  type="button"
                                  onClick={(e) => { e.preventDefault(); setLightbox({ srcs: allSrcs, index: startIdx }); }}
                                  data-testid={`team-msg-att-img-${m.id}-${ai}`}
                                  className="block h-24 w-24 overflow-hidden rounded-md border-2 border-white/40 hover:border-white cursor-zoom-in">
                                  <img src={src} alt={a.filename || "photo"} className="h-full w-full object-cover" loading="lazy"/>
                                </button>
                                {/* Markup pencil — opens PhotoMarkupModal so office
                                    can annotate and re-post as a new message. */}
                                <button
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMarkupSrc(src); }}
                                  data-testid={`team-msg-att-markup-${m.id}-${ai}`}
                                  title="Mark up this photo"
                                  className="absolute top-1 right-1 h-6 w-6 inline-flex items-center justify-center rounded-md bg-[#0f172a]/85 hover:bg-[#0f172a] text-white opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity">
                                  <Pencil weight="bold" size={12}/>
                                </button>
                                {hasGeo && (
                                  <a href={mapUrl} target="_blank" rel="noreferrer"
                                    data-testid={`team-msg-att-geo-${m.id}-${ai}`}
                                    title={`Open in Google Maps${a.accuracy_m ? ` (±${Math.round(a.accuracy_m)}m)` : ""}`}
                                    className={`absolute bottom-1 left-1 right-1 inline-flex items-center justify-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-black uppercase tracking-widest ${mine ? "bg-white/85 text-[#0f172a]" : "bg-[#0f172a]/85 text-white"}`}>
                                    📍 Location
                                  </a>
                                )}
                              </div>
                            );
                          }
                          if (a.media_type === "audio") {
                            return (
                              <div key={ai}
                                data-testid={`team-msg-att-audio-${m.id}-${ai}`}
                                className={`inline-flex items-center gap-2 px-2 py-1.5 rounded-md min-w-[240px] ${mine ? "bg-white/15" : "bg-slate-200"}`}>
                                <Microphone weight="fill" size={14} className={mine ? "text-white" : "text-[#0f172a]"}/>
                                <audio controls preload="metadata" src={src} className="h-8 max-w-[220px]"/>
                              </div>
                            );
                          }
                          return (
                            <a key={ai} href={src} target="_blank" rel="noreferrer"
                              data-testid={`team-msg-att-file-${m.id}-${ai}`}
                              className={`inline-flex items-center gap-1.5 px-2 py-1.5 rounded-md text-[11px] font-bold ${mine ? "bg-white/15 hover:bg-white/25" : "bg-slate-200 hover:bg-slate-300 text-slate-900"}`}>
                              <FileIcon weight="fill" size={14}/>
                              <span className="truncate max-w-[160px]">{a.filename || "file"}</span>
                            </a>
                          );
                        })}
                      </div>
                    )}
                    {/* Read receipts — only on MY messages. Uses the server's
                        `read_by` array (already tracked for unread counts).
                        Ciarra asked for who-saw-it so techs stop double-posting
                        the same photo in Kraaft when nobody replies in FR. */}
                    {mine && (() => {
                      const others = (m.read_by || []).filter(uid => uid !== user?.id);
                      if (others.length === 0) return null;
                      const label = readerLabel(others, roster);
                      return (
                        <div
                          data-testid={`team-msg-read-${m.id}`}
                          className="text-[9px] font-black uppercase tracking-widest mt-1 text-white/70 text-right">
                          ✓ Seen by {label}
                        </div>
                      );
                    })()}
                  </div>
                  {/* Reaction pips + picker — sit OUTSIDE the bubble so they
                      hang under the message like Slack/iMessage. 1-tap ack
                      for office (👍 ❤️ 😂 🚨 ✅) — no typing required.
                      Hidden on ClickUp mirror messages (CU handles reactions
                      through its own API, out of scope for the mirror). */}
                  {!m._cu && (() => {
                    const rx = m.reactions || {};
                    const emojisWithCount = Object.entries(rx).filter(([, list]) => (list || []).length > 0);
                    const picking = reactPickerFor === m.id;
                    return (
                      <div className={`flex items-center gap-1 mt-1 flex-wrap ${mine ? "justify-end" : ""}`}
                           data-testid={`team-msg-reactions-${m.id}`}>
                        {emojisWithCount.map(([emoji, list]) => {
                          const iReacted = (list || []).includes(user?.id);
                          return (
                            <button key={emoji}
                              onClick={() => toggleReact(m, emoji)}
                              data-testid={`team-msg-reaction-${m.id}-${emoji}`}
                              title={iReacted ? "Tap to unreact" : "Tap to add"}
                              className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full border text-[11px] font-bold transition ${
                                iReacted
                                  ? "bg-[#00C4CC]/15 border-[#00C4CC] text-[#0F1F3A]"
                                  : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"
                              }`}>
                              <span>{emoji}</span>
                              <span className="tabular-nums">{list.length}</span>
                            </button>
                          );
                        })}
                        {picking ? (
                          <div className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded-full bg-white border-2 border-[#00C4CC] shadow-md"
                               data-testid={`team-msg-react-picker-${m.id}`}>
                            {REACTIONS.map(e => (
                              <button key={e}
                                onClick={() => toggleReact(m, e)}
                                data-testid={`team-msg-react-pick-${m.id}-${e}`}
                                className="h-6 w-6 rounded-full hover:bg-slate-100 text-base leading-none transition">
                                {e}
                              </button>
                            ))}
                            <button onClick={() => setReactPickerFor(null)}
                              className="h-6 w-6 rounded-full text-slate-400 hover:bg-slate-100 text-xs"
                              aria-label="Close">×</button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setReactPickerFor(m.id)}
                            data-testid={`team-msg-react-open-${m.id}`}
                            title="React"
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-white border border-slate-200 text-slate-400 hover:text-slate-700 hover:border-slate-400 text-[11px] font-bold opacity-60 hover:opacity-100 transition">
                            <span className="text-sm leading-none">☺</span>
                            <span className="text-[9px]">+</span>
                          </button>
                        )}
                      </div>
                    );
                  })()}
                  </div>
                </div>
                </React.Fragment>
              );
            })}
          </div>

          <div className="p-3 md:p-4 border-t-2 border-slate-100 relative">
            {/* Typing pips — small text row above the input showing whoever
                is currently drafting in this thread. Refreshes on the same
                cadence as message polling. */}
            {typers.length > 0 && (
              <div data-testid="team-typing-row"
                className="flex items-center gap-2 text-[11px] text-slate-500 font-bold italic mb-1.5 pl-1">
                <span className="inline-flex items-center gap-0.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00C4CC] animate-pulse"/>
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00C4CC] animate-pulse" style={{ animationDelay: "150ms" }}/>
                  <span className="h-1.5 w-1.5 rounded-full bg-[#00C4CC] animate-pulse" style={{ animationDelay: "300ms" }}/>
                </span>
                <span data-testid="team-typing-label">{typingLabel(typers)}</span>
              </div>
            )}
            {/* Recording overlay — replaces the input row while a voice
                note is being captured. Live seconds counter + red pulse. */}
            {recording && (
              <div data-testid="team-record-overlay"
                className="mb-2 flex items-center gap-3 px-3 py-2 rounded-lg bg-red-50 border-2 border-red-500">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"/>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-red-600"/>
                </span>
                <span className="text-xs font-black uppercase tracking-widest text-red-800">
                  Recording · {String(Math.floor(recordingSecs / 60)).padStart(2, "0")}:{String(recordingSecs % 60).padStart(2, "0")} / 01:00
                </span>
                <div className="ml-auto flex gap-2">
                  <button onClick={cancelVoiceRecord}
                    data-testid="team-record-cancel"
                    className="px-3 h-8 text-[10px] font-black uppercase tracking-widest rounded-md bg-white border-2 border-slate-300 text-slate-700 hover:bg-slate-100">
                    Cancel
                  </button>
                  <button onClick={stopVoiceRecord}
                    data-testid="team-record-stop"
                    className="px-3 h-8 text-[10px] font-black uppercase tracking-widest rounded-md bg-red-600 text-white hover:bg-red-700">
                    Stop &amp; Attach
                  </button>
                </div>
              </div>
            )}
            {acOpen && (userMatches.length + propResults.length + jobResults.length) > 0 && (
              <div className="absolute bottom-16 left-2 right-2 md:left-4 md:right-4 bg-white border-2 border-[#0f172a] rounded-lg shadow-lg overflow-hidden z-20 max-h-72 overflow-y-auto" data-testid="mention-dropdown">
                {acKind === "user_or_prop" && userMatches.length > 0 && (
                  <>
                    <div className="px-3 py-1 text-[9px] font-black uppercase tracking-widest text-slate-500 bg-slate-50">Teammates</div>
                    {userMatches.map(r => {
                      const isEveryone = r.id === "__everyone__";
                      return (
                        <button key={r.id} onClick={() => pickUser(r)}
                          data-testid={isEveryone ? "mention-user-everyone" : `mention-user-${r.id}`}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-left border-b border-slate-100 ${isEveryone ? "bg-red-50 hover:bg-red-100" : "hover:bg-cyan-50"}`}>
                          <div className={`h-6 w-6 rounded-full text-white text-[10px] font-black flex items-center justify-center ${isEveryone ? "bg-red-600" : "bg-[#00C4CC]"}`}>
                            {isEveryone ? "@" : (r.name || "?")[0].toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className={`font-black text-sm ${isEveryone ? "text-red-700" : ""}`}>{isEveryone ? "@everyone" : r.name}</div>
                            <div className="text-[10px] uppercase tracking-widest text-slate-400">
                              {isEveryone ? "Ping the whole crew — use sparingly" : `${r.role}${r.office_role ? ` · ${r.office_role}` : ""}`}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </>
                )}
                {acKind === "user_or_prop" && propResults.length > 0 && (
                  <>
                    <div className="px-3 py-1 text-[9px] font-black uppercase tracking-widest text-slate-500 bg-fuchsia-50">Properties</div>
                    {propResults.map(p => (
                      <button key={p.key} onClick={() => pickProp(p)}
                        data-testid={`mention-prop-${p.key}`}
                        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-fuchsia-50 text-left border-b border-slate-100">
                        <House weight="fill" size={16} className="text-fuchsia-600"/>
                        <div className="min-w-0 flex-1">
                          <div className="font-black text-sm truncate">{p.address}</div>
                          <div className="text-[10px] uppercase tracking-widest text-slate-400">{p.open} open job{p.open === 1 ? "" : "s"}</div>
                        </div>
                      </button>
                    ))}
                  </>
                )}
                {acKind === "workorder" && jobResults.length > 0 && (
                  <>
                    <div className="px-3 py-1 text-[9px] font-black uppercase tracking-widest text-slate-500 bg-cyan-50">Workorders</div>
                    {jobResults.map(j => (
                      <button key={j.id} onClick={() => pickJob(j)}
                        data-testid={`mention-wo-${j.id}`}
                        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-cyan-50 text-left border-b border-slate-100">
                        <Wrench weight="fill" size={16} className="text-[#00C4CC]"/>
                        <div className="min-w-0 flex-1">
                          <div className="font-black text-sm truncate">{j.wo ? `WO${j.wo} · ` : ""}{j.title}</div>
                          <div className="text-[10px] uppercase tracking-widest text-slate-400 truncate">{j.address} · {j.status}</div>
                        </div>
                      </button>
                    ))}
                  </>
                )}
              </div>
            )}
            <div className="flex gap-2 items-center">
              {/* Hidden file inputs — camera opens rear camera on mobile; attach picks anything */}
              <input ref={cameraRef} type="file" accept="image/*" capture="environment" multiple
                className="hidden" data-testid="team-camera-input"
                onChange={e => { onPickFiles(e.target.files); e.target.value = ""; }}/>
              <input ref={attachRef} type="file"
                accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt" multiple
                className="hidden" data-testid="team-attach-input"
                onChange={e => { onPickFiles(e.target.files); e.target.value = ""; }}/>
              <button type="button" onClick={() => cameraRef.current?.click()}
                data-testid="team-camera-btn" title="Take a photo"
                className="tactile-btn h-11 w-11 flex-shrink-0 bg-slate-100 hover:bg-slate-200 border-2 border-[#0f172a] text-slate-900 flex items-center justify-center">
                <Camera weight="fill" size={18}/>
              </button>
              <button type="button" onClick={() => attachRef.current?.click()}
                data-testid="team-attach-btn" title="Attach file"
                className="tactile-btn h-11 w-11 flex-shrink-0 bg-slate-100 hover:bg-slate-200 border-2 border-[#0f172a] text-slate-900 flex items-center justify-center">
                <Paperclip weight="bold" size={18}/>
              </button>
              {/* Voice memo — hold to record, release to send. Falls back
                  to a click on desktop if press-and-hold isn't natural. */}
              <button type="button"
                onMouseDown={startVoiceRecord}
                onMouseUp={stopVoiceRecord}
                onMouseLeave={() => { if (recording) stopVoiceRecord(); }}
                onTouchStart={(e) => { e.preventDefault(); startVoiceRecord(); }}
                onTouchEnd={(e) => { e.preventDefault(); stopVoiceRecord(); }}
                data-testid="team-mic-btn"
                title="Hold to record voice memo"
                className={`tactile-btn h-11 w-11 flex-shrink-0 border-2 flex items-center justify-center transition ${
                  recording
                    ? "bg-red-500 border-red-700 text-white animate-pulse"
                    : "bg-slate-100 hover:bg-slate-200 border-[#0f172a] text-slate-900"
                }`}>
                <Microphone weight="fill" size={18}/>
              </button>
              <input ref={inputRef} value={text} onChange={onTextChange}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                data-testid="team-msg-input"
                placeholder={pending.length > 0
                  ? `Caption this ${pending.length === 1 ? (pending[0].kind === "image" ? "photo" : "file") : `${pending.length} items`}… (what is it? where?)`
                  : `Message… (@ = teammate/property, # = workorder)`}
                maxLength={2000}
                className={`flex-1 min-w-0 h-11 px-3 border-2 text-sm ${pending.length > 0 && !text.trim() ? "border-amber-500 bg-amber-50" : "border-[#0f172a]"}`}/>
              <button onClick={send} disabled={busy || (!text.trim() && pending.length === 0)} data-testid="team-msg-send"
                className="tactile-btn h-11 px-3 md:px-5 bg-[#00C4CC] hover:bg-[#00a8af] text-white font-black uppercase text-xs tracking-widest disabled:opacity-50 flex items-center gap-1 md:gap-2 flex-shrink-0">
                <PaperPlaneTilt weight="bold" size={16}/> <span className="hidden md:inline">Send</span>
              </button>
            </div>
            {pending.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2" data-testid="team-pending-atts">
                {pending.map((p, i) => (
                  <div key={i} className="relative h-16 w-16 border-2 border-[#0f172a] bg-slate-100 flex items-center justify-center overflow-hidden rounded-md" data-testid={`team-pending-${i}`}>
                    {p.kind === "audio" ? (
                      <div className="flex flex-col items-center justify-center text-[#00C4CC]" title="Voice memo">
                        <Microphone weight="fill" size={22}/>
                        <span className="text-[8px] font-black uppercase tracking-widest text-slate-600 mt-0.5">Voice</span>
                      </div>
                    ) : p.previewUrl ? (
                      <img src={p.previewUrl} alt="" className="h-full w-full object-cover"/>
                    ) : (
                      <div className="text-[9px] font-black uppercase tracking-widest text-slate-600 text-center px-1 leading-tight">
                        {p.file.name.split(".").pop().slice(0, 4)}
                      </div>
                    )}
                    <button type="button" onClick={() => removePending(i)}
                      data-testid={`team-pending-remove-${i}`}
                      className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-600 text-white text-[11px] flex items-center justify-center border-2 border-white shadow">
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            {pending.length > 0 && !text.trim() && (
              <div className="mt-1.5 text-[11px] font-bold text-amber-700 flex items-center gap-1.5" data-testid="team-caption-hint">
                <span className="text-base leading-none">💡</span>
                <span>Type what this is (unit, room, damage) so office knows without opening — untitled photos get lost.</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* DM picker modal */}
      {dmPickerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white w-full max-w-md rounded-2xl border-4 border-[#0f172a] shadow-2xl overflow-hidden" data-testid="dm-picker">
            <div className="p-4 border-b-2 border-slate-100 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Direct message</div>
                <div className="font-display font-black text-xl">Pick a teammate</div>
              </div>
              <button onClick={() => setDmPickerOpen(false)} className="p-1">
                <Users weight="bold" size={22}/>
              </button>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {roster.map(r => (
                <button key={r.id} onClick={() => openDM(r)}
                  data-testid={`dm-pick-${r.id}`}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-cyan-50 text-left border-b border-slate-100">
                  <div className="h-9 w-9 rounded-full bg-[#00C4CC] text-white font-black flex items-center justify-center">
                    {(r.name || "?")[0].toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-black">{r.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400">
                      {r.role}{r.office_role ? ` · ${r.office_role}` : ""}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Photo markup modal — reused from job photos, saves to chat instead */}
      {markupSrc && (
        <PhotoMarkupModal
          src={markupSrc}
          jobId={null}
          returnBlob={true}
          onClose={() => setMarkupSrc(null)}
          onSaved={(payload) => { setMarkupSrc(null); if (payload?.blob) saveMarkupToChat(payload); }}
        />
      )}
      {/* Lightbox — full-screen image viewer. Backdrop click OR ESC closes;
          ← / → paginate through every image in the current thread. */}
      {lightbox && (() => {
        const { srcs, index } = lightbox;
        const total = srcs.length;
        const current = srcs[index];
        const go = (delta) => setLightbox(prev => prev ? { ...prev, index: (prev.index + delta + total) % total } : prev);
        return (
          <div
            data-testid="chat-lightbox"
            onClick={() => setLightbox(null)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setLightbox(null);
              else if (e.key === "ArrowLeft") go(-1);
              else if (e.key === "ArrowRight") go(1);
            }}
            tabIndex={-1}
            autoFocus
            ref={(el) => { if (el) el.focus(); }}
            className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center outline-none"
          >
            <button
              onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
              data-testid="chat-lightbox-close"
              className="absolute top-4 right-4 h-10 w-10 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white text-xl font-black"
              title="Close (Esc)">
              <X weight="bold" size={22}/>
            </button>
            {total > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); go(-1); }}
                  data-testid="chat-lightbox-prev"
                  className="absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white text-2xl font-black"
                  title="Previous (←)">‹</button>
                <button
                  onClick={(e) => { e.stopPropagation(); go(1); }}
                  data-testid="chat-lightbox-next"
                  className="absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white text-2xl font-black"
                  title="Next (→)">›</button>
                <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-white/10 text-white text-[10px] font-black uppercase tracking-widest">
                  {index + 1} / {total}
                </div>
              </>
            )}
            <img
              src={current}
              alt="Photo"
              onClick={(e) => e.stopPropagation()}
              data-testid="chat-lightbox-img"
              className="max-h-[92vh] max-w-[92vw] object-contain shadow-2xl"
            />
            <a href={current} target="_blank" rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              data-testid="chat-lightbox-open"
              className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 h-9 inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-[10px] font-black uppercase tracking-widest">
              Open original ↗
            </a>
          </div>
        );
      })()}
    </Shell>
  );
}
